import { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { useTranslation } from 'react-i18next';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';

// Moldura do tema snow (toolbar fixa em cima): caixa com borda, usada em
// formulários e modais.
const MOLDURA_SNOW = 'border border-gray-200 rounded-xl overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-100 [&_.ql-container]:border-0';

// Tema bubble: sem moldura nem toolbar fixa — a formatação aparece num balão ao
// selecionar texto. É o que faz sentido dentro de um documento (editor de
// propostas), onde três toolbars empilhadas pesavam mais que o conteúdo. O Quill
// força Helvetica 13px no container; aqui o texto herda a fonte da página.
const MOLDURA_BUBBLE = '[&_.ql-container]:border-0 [&_.ql-container]:font-sans [&_.ql-container]:text-sm [&_.ql-editor]:px-0 [&_.ql-editor]:py-1 [&_.ql-editor.ql-blank::before]:left-0 [&_.ql-editor.ql-blank::before]:not-italic [&_.ql-tooltip]:z-30';

// Toolbar enxuta para uso em modal (sem header/imagem/code-block): o upload de
// imagem sobe pro path da widget-cms, que não faz sentido em outros contextos.
const TOOLBAR_COMPACTA = [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean']
];

const TOOLBAR_COMPLETA = [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
];

// Editor rich text com upload de imagem pro S3 (evita base64 embutido no corpo,
// que o backend rejeita acima de 300KB)
export default function RichEditor({ value, onChange, placeholder, className, minHeight = 220, compacto, readOnly, theme = 'snow' }) {
    const { t } = useTranslation('settings');
    const quillRef = useRef(null);

    const modules = useMemo(() => ({
        toolbar: {
            container: compacto ? TOOLBAR_COMPACTA : TOOLBAR_COMPLETA,
            handlers: {
                image: function handleImage() {
                    const quill = quillRef.current?.getEditor();
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async () => {
                        const file = input.files?.[0];
                        if (!file || !quill) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                            const res = await v2_actionUploadS3(formData, '?path=plataforma/widget-cms');
                            const url = res?.data?.data?.location;
                            if (url) {
                                const range = quill.getSelection(true);
                                quill.insertEmbed(range.index, 'image', url, 'user');
                                quill.setSelection(range.index + 1);
                            }
                        } catch (e) {
                            console.log('erro ao fazer upload da imagem', e);
                        }
                    };
                    input.click();
                }
            }
        },
        clipboard: { matchVisual: false }
    }), [compacto]);

    return (
        <div
            className={`widget-cms-editor ${theme === 'bubble' ? MOLDURA_BUBBLE : MOLDURA_SNOW} [&_.ql-editor]:min-h-[var(--rich-editor-min-h)] ${className || ''}`}
            style={{ '--rich-editor-min-h': `${minHeight}px` }}
        >
            <ReactQuill
                ref={quillRef}
                theme={theme}
                readOnly={readOnly}
                value={value || ''}
                onChange={onChange}
                modules={modules}
                placeholder={placeholder || t('widgetCms.richEditor.defaultPlaceholder', 'Escreva o conteúdo...')}
            />
        </div>
    );
}

RichEditor.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    minHeight: PropTypes.number,
    compacto: PropTypes.bool,
    readOnly: PropTypes.bool,
    theme: PropTypes.oneOf(['snow', 'bubble'])
};
